import { TREATMENTS_COUNT_MONTH } from '../utils/constants';

// Type definitions for metrics
export interface Metrics {
    clinicCount: number;
    totalReviews: number;
    stateCount: number;
    treatmentsPerMonth: number;
}

export interface FormattedMetrics {
    clinicCount: string;
    totalReviews: string;
    stateCount: string;
    treatmentsPerMonth: string;
}

interface FirebaseStore {
    open?: boolean;
    reviewCount?: number | null;
}

interface FirebaseMetro {
    stores?: FirebaseStore[];
}

interface FirebaseStateData {
    abbreviation: string;
    metros?: FirebaseMetro[];
}

// Type for formatter functions
type VariableFormatter = (value: number) => string;
type VariableFormatters = Record<keyof Metrics, VariableFormatter>;

// Formatters for converting metric values to localized strings
export const METRIC_FORMATTERS: VariableFormatters = {
    clinicCount: (value: number) => (value ? `${value.toLocaleString()}+` : '0'),
    totalReviews: (value: number) => (value ? value.toLocaleString() : '0'),
    stateCount: (value: number) => (value ? value.toLocaleString() : '0'),
    treatmentsPerMonth: (value: number) => (value ? value.toLocaleString() : '0'),
};

// Calculates clinic count, reviews, states, and treatments from Firebase store data
export function calculateMetrics(milanStores: FirebaseStateData[], globalData?: Record<string, unknown>): Metrics {
    if (!milanStores || !Array.isArray(milanStores)) {
        return {
            clinicCount: 0,
            totalReviews: 0,
            stateCount: 0,
            treatmentsPerMonth: 0,
        };
    }

    let clinicCountRaw = 0;
    let totalReviews = 0;
    const statesWithClinics = new Set<string>();

    milanStores.forEach((state: FirebaseStateData) => {
        if (!state.metros || !Array.isArray(state.metros)) {
            return;
        }

        let stateHasOpenClinics = false;

        state.metros.forEach((metro: FirebaseMetro) => {
            if (!metro.stores || !Array.isArray(metro.stores)) {
                return;
            }

            metro.stores.forEach((store: FirebaseStore) => {
                if (store.open !== false) {
                    clinicCountRaw++;
                    stateHasOpenClinics = true;

                    if (store.reviewCount && typeof store.reviewCount === 'number') {
                        totalReviews += store.reviewCount;
                    }
                }
            });
        });

        if (stateHasOpenClinics && state.abbreviation) {
            statesWithClinics.add(state.abbreviation);
        }
    });

    const stateCount = statesWithClinics.size;

    // Round clinic count to nearest 10
    const clinicCount = clinicCountRaw - (clinicCountRaw % 10);

    // Get treatments per month from globalData or use default
    const treatmentsPerMonth = ((globalData?.tx_per_month as number) || TREATMENTS_COUNT_MONTH) * 1000;

    return {
        clinicCount,
        totalReviews,
        stateCount,
        treatmentsPerMonth,
    };
}
